/**
 * AC-146 W9 (2026-07-14): PDP Color Gallery modal — defines <pdp-color-gallery>.
 * Rendered by snippets/pdp-color-gallery.liquid as a SIBLING of <variant-picker>
 * (blocks/variant-picker.liquid), so the picker's morph re-render never destroys
 * the dialog. Selection uses ONLY the documented variant-picker hook: check the
 * matching radio and dispatch a bubbling `change` — variant-picker.js does the
 * rest (media swap, URL `?variant=`, morph). This module never imports from
 * variant-picker.js.
 */
import { DialogComponent } from '@theme/dialog';

/**
 * A custom element that renders the PDP color-gallery dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends DialogComponent
 */
class PdpColorGallery extends DialogComponent {
  requiredRefs = ['dialog'];

  connectedCallback() {
    super.connectedCallback();
    // Native showModal() lets Tab reach browser chrome after the last focusable;
    // wrap explicitly so focus cycles within the dialog (a11y parity with the
    // theme's other modals).
    this.refs.dialog?.addEventListener('keydown', this.#handleTrapKeydown);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.dialog?.removeEventListener('keydown', this.#handleTrapKeydown);
  }

  /**
   * Locates the sibling <variant-picker>, re-queried on EVERY call — the picker
   * morph re-renders itself on each option change, so a cached reference would
   * go stale. The block renders this element and <variant-picker> as direct
   * siblings (blocks/variant-picker.liquid), so the parent lookup is primary;
   * the section-level lookup is a fallback for wrapper changes.
   *
   * @returns {Element | null} The sibling variant picker, if any.
   */
  #getVariantPicker() {
    return (
      this.parentElement?.querySelector('variant-picker') ??
      this.closest('section, .shopify-section, .shopify-block')?.querySelector('variant-picker') ??
      null
    );
  }

  /**
   * Wraps Tab focus at the dialog edges while it is open.
   *
   * @param {KeyboardEvent} event - The keydown event.
   */
  #handleTrapKeydown = (event) => {
    if (event.key !== 'Tab' || !this.refs.dialog.open) return;

    const focusables = Array.from(
      this.refs.dialog.querySelectorAll('button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')
    );
    if (focusables.length === 0) return;

    const first = focusables[0];
    const last = focusables[focusables.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      /** @type {HTMLElement} */ (last).focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      /** @type {HTMLElement} */ (first).focus();
    }
  };

  /**
   * Opens the dialog, first syncing the highlighted tile to the picker's
   * currently-checked color radio (the Liquid-rendered `aria-pressed` only
   * reflects the state at page render).
   */
  showDialog() {
    this.#syncSelectedTile();
    super.showDialog();
  }

  /**
   * Highlights the tile matching the picker's checked color radio.
   */
  #syncSelectedTile() {
    const checkedInput = this.#getVariantPicker()?.querySelector(
      'input[type="radio"][data-option-value-id]:checked'
    );
    const selectedId = checkedInput instanceof HTMLElement ? checkedInput.dataset.optionValueId : undefined;

    for (const tile of this.refs.dialog.querySelectorAll('button[data-option-value-id]')) {
      tile.setAttribute('aria-pressed', `${tile.dataset.optionValueId === selectedId}`);
    }
  }

  /**
   * Handles a tile click: selects the matching color in the sibling
   * <variant-picker> and closes the dialog.
   *
   * @param {MouseEvent} event - The click event.
   */
  selectColor(event) {
    const tile = event.target instanceof Element ? event.target.closest('button[data-option-value-id]') : null;
    if (!(tile instanceof HTMLElement) || !tile.dataset.optionValueId) return;

    const input = this.#getVariantPicker()?.querySelector(
      `input[data-option-value-id="${tile.dataset.optionValueId}"]`
    );

    if (input instanceof HTMLInputElement && !input.checked) {
      input.checked = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    this.#syncSelectedTile();
    this.closeDialog();
  }
}

if (!customElements.get('pdp-color-gallery')) {
  customElements.define('pdp-color-gallery', PdpColorGallery);
}
